import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
// ⚠️ Deep import of the .desktop variant is REQUIRED (ledger hazard 16): NOTIFICATION_VARIABLES
// is DIVERGENT (web 11 keys, mobile 5), and a bare folder import types it against index.native.
import { NOTIFICATION_VARIABLES } from "../../../data/constants";
// ⚠️ Deep import of the .desktop variant is REQUIRED (ledger hazard 6): a bare folder import
// typechecks against index.native, whose LoginSlice exports a different surface.
import { supportLogout, userLogout } from "../LoginSlice";
import {
  sharedInitialState,
  sharedReducers,
  type SharedNotificationsState,
} from "./shared";

export interface NotificationsState extends SharedNotificationsState {
  showStopNotificationNudge : boolean;
  isNotificationStop: boolean;
  allNewOnlinePollingOrders: any;
}

const initialState: NotificationsState = {
  ...sharedInitialState,
  showStopNotificationNudge: false,
  isNotificationStop: false,
  allNewOnlinePollingOrders: [],
};

export const notificationsSlice = createSlice({
  name: "notificationSlice",
  initialState,
  reducers: {
    ...sharedReducers,
    clearAllNotifications: (state: NotificationsState) => {
      state.allOtherNotifications = [];
      state.allNewOnlineOrders = [];
    },
    // The next four write NOTIFICATION_VARIABLES where mobile writes the equivalent string
    // literal. Same values, but mobile's constant defines no `rejected`/`approved` — see shared.ts.
    markAsReadNotificationSuccess: (state: NotificationsState, action: PayloadAction<any>) => {
      const messageId = action.payload.messageIds[0];
      state.allOtherNotifications.results =
        state.allOtherNotifications.results?.map((notification: any) => {
          if (notification.messageId === messageId) {
            return { ...notification, status: NOTIFICATION_VARIABLES.read };
          }
          return notification;
        });
    },
    markAllAsReadSuccess: (state: NotificationsState) => {
      state.allOtherNotifications.results =
        state.allOtherNotifications.results?.map((notification: any) => ({
          ...notification,
          status: NOTIFICATION_VARIABLES.read,
        }));
    },
    acceptApproveOrderSuccess: (state: NotificationsState, action: PayloadAction<any>) => {
      const messageId = action.payload.messageId;
      state.allOtherNotifications.results =
        state.allOtherNotifications.results?.map((notification: any) => {
          if (notification.messageId === messageId) {
            return {
              ...notification,
              actionStatus: NOTIFICATION_VARIABLES.approved,
            };
          }
          return notification;
        });
    },
    rejectApproveOrderSuccess: (state: NotificationsState, action: PayloadAction<any>) => {
      const messageId = action.payload.messageId;
      state.allOtherNotifications.results =
        state.allOtherNotifications.results?.map((notification: any) => {
          if (notification.messageId === messageId) {
            return {
              ...notification,
              actionStatus: NOTIFICATION_VARIABLES.rejected,
            };
          }
          return notification;
        });
    },
    // 2-arity on web (5 dispatch sites pass a payload), 0-arity on mobile. See shared.ts.
    getAllNewOnlineOrders: (state: NotificationsState, action: PayloadAction<any>) => {},
    getAllNewOnlinePollingOrdersSuccess: (state: NotificationsState, action: PayloadAction<any>) => {
      state.allNewOnlinePollingOrders = action.payload;
    },
    updateOtherNotification: (state: NotificationsState, action: PayloadAction<any>) => {
      state.allOtherNotifications.results = [
        action.payload,
        ...state.allOtherNotifications.results,
      ];
    },
    showStopNotificationNudge: (state: NotificationsState, action: PayloadAction<any>) => {
      state.showStopNotificationNudge = action.payload
    },
    notificationCurrentStatus: (state: NotificationsState, action: PayloadAction<any>) => {
      state.isNotificationStop = action.payload
    },
    // 0-arity on web (`dispatch(testNotification())`), 2-arity on mobile. See shared.ts.
    testNotification: () => {},
    postActivityLog: (state: NotificationsState, action: PayloadAction<any>) => {},
  },
  extraReducers: (builder) => {
    builder
      // ⚠️ BOTH CASES ARE EMPTY ON PURPOSE — verbatim from apps/web, where each body is
      // whitespace only. The cases ARE registered (the actions are handled and produce a new
      // draft) but change nothing. Preserved exactly; this slice is one of the few web slices
      // that does NOT reset on logout, and "fixing" it would change web's logout behaviour.
      .addCase(userLogout, (state) => {
        
      })
      .addCase(supportLogout, (state) => {
        
      });
  },
});

export const {
  toggleOnlineNotificationsModal,
  clearAllNewOnlineOrders,
  removeAcceptedOrder,
  removeOrderFromNotifications,
  storeCurrOtherNotifications,
  clearAllNotifications,
  closeNotifications,
  getAllOtherNotifications,
  getOtherNotifications,
  markAsReadNotification,
  markAsReadNotificationSuccess,
  markAllAsReadSuccess,
  markAllAsRead,
  acceptApproveOrder,
  acceptApproveOrderSuccess,
  rejectApproveOrder,
  rejectApproveOrderSuccess,
  viewOrderBill,
  viewOrderBillSuccess,
  viewPurchaseOrder,
  viewPurchaseOrderSuccess,
  viewFeedback,
  viewFeedbackSuccess,
  getAllNewOnlineOrders,
  getAllNewOnlineOrdersSuccess,
  getAllNewOnlinePollingOrdersSuccess,
  updateOtherNotification,
  showStopNotificationNudge,
  notificationCurrentStatus,
  testNotification,
  postActivityLog,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
